import React, { useState, useMemo } from 'react';
import { Loader2, Plus, X, Scissors } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';

// Split one stored box into N child boxes by per-SKU qty. `box` has items[]
// {barcode, design_name, shade, size, qty} and box_number/total_qty.
export const SplitBoxDialog = ({ box, onClose, onDone }) => {
  const lines = (box?.items || []).filter((it) => (it.barcode || '') && (it.qty || 0) > 0);
  const [boxes, setBoxes] = useState([{}]); // array of { [barcode]: qtyString }
  const [codes, setCodes] = useState(['']); // pre-printed box code per child
  const [busy, setBusy] = useState(false);

  const avail = useMemo(() => {
    const m = {};
    lines.forEach((it) => { m[it.barcode] = (m[it.barcode] || 0) + Number(it.qty || 0); });
    return m;
  }, [lines]);

  const num = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) && n > 0 ? n : 0; };
  const allocated = (bc) => boxes.reduce((s, b) => s + num(b[bc]), 0);
  const remaining = (bc) => (avail[bc] || 0) - allocated(bc);
  const childTotal = (ci) => Object.values(boxes[ci] || {}).reduce((s, v) => s + num(v), 0);

  const setQty = (ci, bc, val) => setBoxes((prev) => {
    const next = prev.map((b) => ({ ...b }));
    if (val === '' ) delete next[ci][bc]; else next[ci][bc] = val;
    return next;
  });
  const addBox = () => { setBoxes((p) => [...p, {}]); setCodes((p) => [...p, '']); };
  const removeBox = (ci) => {
    if (boxes.length <= 1) return;
    setBoxes((p) => p.filter((_, i) => i !== ci));
    setCodes((p) => p.filter((_, i) => i !== ci));
  };

  const overAlloc = lines.some((it) => remaining(it.barcode) < 0);
  const anyChild = boxes.some((_, ci) => childTotal(ci) > 0);
  const codeMissing = boxes.some((_, ci) => childTotal(ci) > 0 && !(codes[ci] || '').trim());
  const totalSplit = boxes.reduce((s, _, ci) => s + childTotal(ci), 0);
  const parentLeft = (box?.total_qty || 0) - totalSplit;

  const submit = async () => {
    if (!anyChild) { toast.error('Enter a quantity for at least one child box'); return; }
    if (overAlloc) { toast.error('A quantity exceeds what is available'); return; }
    if (codeMissing) { toast.error('Scan a box code for every child box'); return; }
    const children = boxes
      .map((b, ci) => ({
        lines: Object.entries(b).map(([barcode, q]) => ({ barcode, qty: num(q) })).filter((l) => l.qty > 0),
        box_code: (codes[ci] || '').trim() || undefined,
      }))
      .filter((c) => c.lines.length > 0);    setBusy(true);
    try {
      const r = await api.splitBox(box.box_number, children);
      toast.success(r.message);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Split failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="ssr-split-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-fuchsia-600" /> Split Box {box?.box_number}
          </DialogTitle>
          <DialogDescription>Carve this box into smaller child boxes by quantity.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-xs text-[#5A5F6E]">
            {box?.total_qty} pcs · slug {box?.slug || '—'}. Carve this box into child boxes by quantity —
            each child becomes its own stored box (new number + sticker) in the same bin; the remainder stays in{' '}
            <b>{box?.box_number}</b>.
          </p>

          <div className="overflow-x-auto rounded-md border border-[#EFE9DC]">
            <table className="w-full text-xs" data-testid="ssr-split-grid">
              <thead className="bg-[#F5F1E8] text-[#5A5F6E]">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium min-w-[180px]">SKU</th>
                  <th className="px-2 py-1.5 font-medium text-right">Avail</th>
                  {boxes.map((_, ci) => (
                    <th key={ci} className="px-2 py-1.5 font-medium text-center min-w-[92px]">
                      <div className="flex items-center justify-center gap-1">
                        Box {ci + 1}
                        {boxes.length > 1 && (
                          <button onClick={() => removeBox(ci)} className="text-red-400 hover:text-red-600" data-testid={`ssr-split-removebox-${ci}`}>
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-2 py-1.5 font-medium text-right">Left</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EADD] bg-white">
                <tr className="bg-[#FBF9F3]" data-testid="ssr-split-coderow">
                  <td className="px-2 py-1.5 text-[11px] text-[#8A8F9E]">Box code (scan sticker) <span className="text-red-500 font-semibold">*</span></td>
                  <td />
                  {boxes.map((_, ci) => {
                    const needCode = childTotal(ci) > 0 && !(codes[ci] || '').trim();
                    return (
                      <td key={ci} className="px-1 py-1">
                        <Input value={codes[ci] || ''}
                          onChange={(e) => setCodes((p) => { const n = [...p]; n[ci] = e.target.value; return n; })}
                          placeholder="scan sticker"
                          className={`h-8 text-center px-1 text-[11px] ${needCode ? 'border-red-400 ring-1 ring-red-300 focus-visible:ring-red-400' : ''}`}
                          data-testid={`ssr-split-code-${ci}`} />
                      </td>
                    );
                  })}
                  <td />
                </tr>

                {lines.map((it) => (
                  <tr key={it.barcode} data-testid={`ssr-split-row-${it.barcode}`}>
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{it.design_name || it.description || '—'}</div>
                      <div className="text-[10px] text-[#8A8F9E] font-mono">{it.barcode} · {it.shade || '—'} · {it.size || '—'}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{avail[it.barcode] || 0}</td>
                    {boxes.map((b, ci) => (
                      <td key={ci} className="px-1 py-1">
                        <Input
                          type="number" min="0" value={b[it.barcode] ?? ''}
                          onChange={(e) => setQty(ci, it.barcode, e.target.value)}
                          className="h-8 text-center px-1"
                          data-testid={`ssr-split-input-${ci}-${it.barcode}`}
                        />
                      </td>
                    ))}
                    <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${remaining(it.barcode) < 0 ? 'text-red-600' : 'text-[#2D5F4F]'}`} data-testid={`ssr-split-left-${it.barcode}`}>
                      {remaining(it.barcode)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#FAF7F0] text-[#1A1F2E] font-semibold">
                <tr>
                  <td className="px-2 py-1.5">Box total</td>
                  <td className="px-2 py-1.5 text-right">{box?.total_qty || 0}</td>
                  {boxes.map((_, ci) => (
                    <td key={ci} className="px-2 py-1.5 text-center tabular-nums" data-testid={`ssr-split-childtotal-${ci}`}>{childTotal(ci)}</td>
                  ))}
                  <td className="px-2 py-1.5 text-right tabular-nums">{parentLeft}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={addBox} data-testid="ssr-split-addbox">
              <Plus className="w-4 h-4 mr-1" /> Add child box
            </Button>
            <p className="text-xs text-[#5A5F6E]" data-testid="ssr-split-summary">
              {overAlloc ? (
                <>Fix the highlighted quantities — some exceed availability</>
              ) : codeMissing ? (
                <>Scan a <b>box code</b> for every child box</>
              ) : (
                <>Splitting <b>{totalSplit}</b> pcs into <b>{boxes.filter((_, ci) => childTotal(ci) > 0).length}</b> box(es) ·
                  {parentLeft > 0 ? <> <b>{parentLeft}</b> pcs stay in {box?.box_number}</> : <> {box?.box_number} fully split</>}</>
              )}
            </p>
          </div>
          {overAlloc && <p className="text-xs text-red-600" data-testid="ssr-split-error">A quantity exceeds what is available (see the red "Left" values).</p>}
          {!overAlloc && codeMissing && <p className="text-xs text-red-600" data-testid="ssr-split-code-error">Every child box needs a scanned pre-printed box code (see the highlighted boxes).</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()} data-testid="ssr-split-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || overAlloc || !anyChild || codeMissing} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="ssr-split-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scissors className="w-4 h-4 mr-2" />} Split Box
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SplitBoxDialog;
